import { FC, FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { PanelWidth } from "../../../../components/panel/Panel";
import {
  DagMarketOrderType,
  useCreateDagMarketOrderMutation,
  useDagMarketOrdersQuery,
  useDeleteDagMarketOrderMutation,
} from "../../../../graphql/schema";
import { Button } from "../../../../components/button/Button";
import { Color } from "../../../../services/buttonLinkConstants";
import { Modal, ModalKind } from "../../../../components/modal/Modal";
import { showGraphqlValidationErrors } from "../../../../services/showGraphqlValidationErrors";
import { DataFraction } from "../../../../components/data-fraction/DataFraction";

import styles from "./dag-community-view.module.scss";

const MAX_ACTIVE_ORDERS_PER_TYPE = 5;

type LinkedField = "amount" | "price" | "total";

const formatNumber = (n: number): string => {
  if (!isFinite(n) || isNaN(n)) return "";
  return parseFloat(n.toFixed(8)).toString();
};

interface LinkedNumericInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const LinkedNumericInput: FC<LinkedNumericInputProps> = ({ label, value, onChange, error }) => (
  <div style={{ margin: "0 0 18px" }}>
    <label style={{ display: "block", marginBottom: 4, color: "#666", fontSize: 13 }}>{label}</label>
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 4,
        border: `1px solid ${error ? "#c0392b" : "#ccc"}`,
        fontSize: 14,
        boxSizing: "border-box",
        outline: "none",
      }}
    />
    {error && <p style={{ color: "#c0392b", fontSize: 12, margin: "4px 0 0" }}>{error}</p>}
  </div>
);

export const DagCommunityMarket: FC = () => {
  const { t } = useTranslation();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data, refetch, loading } = useDagMarketOrdersQuery({ fetchPolicy: "network-only" });
  const [createDagMarketOrder, { loading: creating }] = useCreateDagMarketOrderMutation();
  const [deleteDagMarketOrder] = useDeleteDagMarketOrderMutation();

  const orders = data?.getDagMarketOrders ?? [];
  const sellOrders = orders.filter((o) => o.orderType === DagMarketOrderType.SELL);

  const activeSellCount = sellOrders.filter((o) => !o.isDone).length;
  const selectedTypeIsFull = activeSellCount >= MAX_ACTIVE_ORDERS_PER_TYPE;

  const [amount, setAmount] = useState("");
  const [price, setPrice] = useState("");
  const [total, setTotal] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ amount?: string; price?: string }>({});

  const clearForm = () => {
    setAmount("");
    setPrice("");
    setTotal("");
    setFieldErrors({});
  };

  const openCreate = () => {
    clearForm();
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    clearForm();
  };

  const handleFieldChange = (field: LinkedField, value: string) => {
    const next = {
      amount: field === "amount" ? value : amount,
      price: field === "price" ? value : price,
      total: field === "total" ? value : total,
    };

    const a = parseFloat(next.amount);
    const p = parseFloat(next.price);
    const tt = parseFloat(next.total);

    if (field === "amount" || field === "price") {
      // Amount and Price are independent inputs; Total reflects amount × price.
      next.total = !isNaN(a) && !isNaN(p) ? formatNumber(a * p) : "";
    } else {
      // Editing Total adjusts Price (using current Amount); Amount stays put.
      next.price = !isNaN(a) && a !== 0 && !isNaN(tt) ? formatNumber(tt / a) : "";
    }

    setAmount(next.amount);
    setPrice(next.price);
    setTotal(next.total);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();

    const errors: typeof fieldErrors = {};
    if (!amount || parseFloat(amount) <= 0) errors.amount = t("Amount must be greater than 0");
    if (!price || parseFloat(price) <= 0) errors.price = t("Price must be greater than 0");
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      const result = await createDagMarketOrder({
        variables: { orderType: DagMarketOrderType.SELL, amount, price },
      });

      if (result.data?.createDagMarketOrder) {
        toast.success(t("Order placed successfully"));
        closeCreate();
        await refetch();
      } else {
        toast.error(result.errors?.[0]?.message ?? t("Something went wrong"));
      }
    } catch (err: any) {
      if (!showGraphqlValidationErrors(t, err) && err instanceof Error) {
        toast.error(err.message);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("Are you sure you want to delete this request?"))) {
      return;
    }
    try {
      const result = await deleteDagMarketOrder({ variables: { id } });
      if (result.data?.deleteDagMarketOrder) {
        toast.success(t("Request deleted"));
        await refetch();
      } else {
        toast.error(result.errors?.[0]?.message ?? t("Could not delete request"));
      }
    } catch (err: any) {
      if (!showGraphqlValidationErrors(t, err) && err instanceof Error) {
        toast.error(err.message);
      }
    }
  };

  const renderOrderList = (title: string, list: typeof orders, activeCount: number) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <h3 style={{ margin: "10px 0" }}>
        {title}{" "}
        <small style={{ color: "#666", fontSize: 14 }}>
          ({activeCount}/{MAX_ACTIVE_ORDERS_PER_TYPE} {t("active")})
        </small>
      </h3>
      {list.length === 0 ? (
        <p style={{ color: "#888" }}>{t("No requests yet.")}</p>
      ) : (
        [...list]
          .sort((a, b) => Number(!!a.isDone) - Number(!!b.isDone))
          .map((order) => (
            <div
              key={order.id}
              className={styles["term-grid-normal"]}
              style={{
                alignItems: "center",
                backgroundColor: order.isDone ? "#e8fce3" : undefined,
              }}
            >
              <div>
                <DataFraction
                  className={styles["data-fraction"]}
                  icon={undefined}
                  label={t("Amount")}
                  value={order.amount}
                />
              </div>
              <div>
                <DataFraction
                  className={styles["data-fraction"]}
                  icon={undefined}
                  label={t("Price")}
                  value={order.price}
                />
              </div>
              <div style={{ textAlign: "right" }}>
                {order.isDone ? (
                  <span style={{ color: "#2a8a3e", fontWeight: 600 }}>{t("Completed")}</span>
                ) : (
                  <Button
                    borderRadius="MEDIUM"
                    color={Color.DARK_BLUE}
                    fontSize={12}
                    onClick={() => handleDelete(order.id)}
                  >
                    <span className={styles["link-label"]}>{t("Delete")}</span>
                  </Button>
                )}
              </div>
            </div>
          ))
      )}
    </div>
  );

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "15px 0" }}>
        <h2 style={{ margin: 0 }}>{t("DAGS to Market")}</h2>
        <Button borderRadius="MEDIUM" color={Color.DARK_BLUE} fontSize={14} onClick={openCreate}>
          <span className={styles["link-label"]}>{t("Add new sell request")}</span>
        </Button>
      </div>

      {loading && <p>{t("Loading...")}</p>}

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        {renderOrderList(t("Sell Requests"), sellOrders, activeSellCount)}
      </div>

      <Modal
        isOpen={isCreateOpen}
        close={closeCreate}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("New sell request"),
          caption: t("New sell request"),
          label: t("Place a new market order"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div>
          <h2 style={{ textAlign: "center", marginTop: "30px" }}>{t("New sell request")}</h2>
          <form onSubmit={handleCreate}>
            <LinkedNumericInput
              label={t("Amount (DAGS)")}
              value={amount}
              onChange={(v) => handleFieldChange("amount", v)}
              error={fieldErrors.amount}
            />

            <LinkedNumericInput
              label={t("Price per DAG")}
              value={price}
              onChange={(v) => handleFieldChange("price", v)}
              error={fieldErrors.price}
            />

            <LinkedNumericInput label={t("Total")} value={total} onChange={(v) => handleFieldChange("total", v)} />

            <p style={{ color: "#666", fontSize: 12, margin: "0 0 15px" }}>
              {t("Total = Amount × Price. Enter Total directly to set the per-DAG price.")}
            </p>

            {selectedTypeIsFull && (
              <p style={{ color: "#c0392b", margin: "10px 0" }}>
                {t("You already have the maximum {{count}} active SELL requests.", {
                  count: MAX_ACTIVE_ORDERS_PER_TYPE,
                })}
              </p>
            )}

            <div style={{ textAlign: "center", width: "100%", alignItems: "center" }}>
              <Button
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                color={Color.DARK_BLUE}
                fontSize={14}
                disabled={creating || selectedTypeIsFull}
              >
                <span className={styles["link-label"]}>{creating ? t("Submitting...") : t("Submit request")}</span>
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};
